import React, { useState, useCallback, useRef, useMemo, useEffect, memo } from "react";
import { View, StyleSheet, LayoutChangeEvent, Pressable, Dimensions } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";

type ArrayChipsProps = {
  values: string[];
  displayMap?: Record<string, string>; // For code -> display name mapping
  colorMap?: Record<string, string>; // For value -> color mapping (per-chip colors)
  chipColor?: string; // Fallback color if no colorMap
  readonlyValues?: string[]; // Values that cannot be edited (e.g., from location)
  readonlyLabel?: string; // Label for readonly chips (e.g., "From Location")
  width?: number;
};

const GAP = 4;
const OVERFLOW_WIDTH = 36;

export const ArrayChips = memo(function ArrayChips({
  values,
  displayMap,
  colorMap,
  chipColor,
  readonlyValues = [],
  readonlyLabel,
  width,
}: ArrayChipsProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const overflowChipRef = useRef<View>(null);

  // Container width is measured from the component itself so it reacts to resizing.
  const [containerWidth, setContainerWidth] = useState<number>(width ?? 0);
  // Measured width of each chip, keyed by value (measured in a hidden layer).
  const [chipWidths, setChipWidths] = useState<Record<string, number>>({});

  const hasMeasuredRef = useRef(false);
  useEffect(() => {
    if (!hasMeasuredRef.current && typeof width === "number" && width > 0) {
      setContainerWidth((prev) => (prev > 0 ? prev : width));
    }
  }, [width]);

  const readonlySet = useMemo(() => new Set(readonlyValues), [readonlyValues]);

  const orderedValues = useMemo(() => {
    const source = values ?? [];
    const validValues = displayMap ? source.filter((v) => displayMap[v]) : source;
    const readonlyItems = validValues.filter((v) => readonlySet.has(v));
    const editableItems = validValues.filter((v) => !readonlySet.has(v));
    return [...readonlyItems, ...editableItems];
  }, [values, displayMap, readonlySet]);

  const getChipColor = useCallback(
    (value: string) => colorMap?.[value] || chipColor || colors.primary,
    [colorMap, chipColor, colors.primary],
  );

  // Measure the real container width and recompute on every resize.
  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0) hasMeasuredRef.current = true;
    setContainerWidth((prev) => (Math.abs(prev - w) > 5 ? w : prev)); // If change > 5px, update
  }, []);

  // Record each chip's natural width from the hidden measuring layer.
  const handleMeasureChip = useCallback((value: string, w: number) => {
    setChipWidths((prev) => (prev[value] === w ? prev : { ...prev, [value]: w }));
  }, []);

  // Calculate how many chips fit, reserving room for the overflow chip when needed.
  const visibleCount = useMemo<number | null>(() => {
    if (containerWidth <= 0 || orderedValues.length === 0) return null;

    const widths = orderedValues.map((v) => chipWidths[v]);
    if (widths.some((w) => w == null)) return null; // wait until all chips measured

    let total = 0;
    for (let i = 0; i < widths.length; i++) {
      total += widths[i] + (i > 0 ? GAP : 0);
    }
    if (total <= containerWidth) return orderedValues.length;

    let used = 0;
    let count = 0;
    for (let i = 0; i < widths.length; i++) {
      const needed = widths[i] + (count > 0 ? GAP : 0);
      if (used + needed + GAP + OVERFLOW_WIDTH <= containerWidth) {
        used += needed;
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [orderedValues, chipWidths, containerWidth]);

  if (orderedValues.length === 0) {
    return null;
  }

  const displayValues = visibleCount !== null ? orderedValues.slice(0, visibleCount) : orderedValues;

  const overflow = visibleCount !== null ? orderedValues.length - visibleCount : 0;

  // everything after the visible chips
  const remainingValues = orderedValues.slice(displayValues.length);

  const renderChip = (value: string, onLayout?: (w: number) => void, keyPrefix = "") => {
    const isReadonly = readonlySet.has(value);
    const valueColor = getChipColor(value);
    return (
      <View
        key={keyPrefix + value}
        onLayout={onLayout ? (e) => onLayout(e.nativeEvent.layout.width) : undefined}
        style={[
          styles.chip,
          isReadonly
            ? {
                backgroundColor: "transparent",
                borderWidth: 1,
                borderStyle: "dashed",
                borderColor: valueColor,
              }
            : { backgroundColor: valueColor },
        ]}
      >
        <ThemedText style={[styles.chipText, isReadonly && { color: valueColor }]} numberOfLines={1}>
          {displayMap?.[value] || value}
          {isReadonly && readonlyLabel ? ` · ${readonlyLabel}` : ""}
        </ThemedText>
      </View>
    );
  };

  const handleHoverIn = () => {
    if (!toolTip || remainingValues.length === 0) return;

    toolTip.setToolTipContent(
      <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
        {remainingValues.map((value) => renderChip(value, undefined, "tooltip-"))}
      </View>,
    );

    // Sets Tooltip location
    overflowChipRef.current?.measureInWindow((x, y, w, h) => {
      let longestValue = Math.max(...remainingValues.map((v) => displayMap![v]).map((str) => str.length));
      const TOOLTIP_PADDING = styles.tooltip.padding;
      const TOOLTIP_GAP = GAP;
      const TOOLTIP_WIDTH = 260;
      const ROW_HEIGHT = 22;
      const n = remainingValues.length;
      const tooltipHeight = TOOLTIP_PADDING * 2 + n * ROW_HEIGHT + Math.max(0, n - 1) * TOOLTIP_GAP;
      const windowHeight = Dimensions.get("window").height;

      // Align the tooltip's right edge with the overflow chip's right edge
      const left = x + (w || 0) - TOOLTIP_WIDTH;

      // Default: show below the overflow chip
      let top = y + (h || 0) + GAP;

      // If not room below, flip the tooltip above the chip
      if (top + tooltipHeight > windowHeight) {
        top = y - tooltipHeight - GAP;
      }

      toolTip.setLocation({
        x: left,
        y: top,
        width: longestValue < 20 ? 150 : TOOLTIP_WIDTH,
        height: tooltipHeight,
      } as any);
      toolTip.setVisible(true);
    });
  };

  const handleHoverOut = () => {
    toolTip?.setVisible(false);
  };

  return (
    <View style={styles.container} onLayout={handleContainerLayout}>
      {displayValues.map((value) => renderChip(value))}
      {overflow > 0 && (
        <Pressable onHoverIn={handleHoverIn} onHoverOut={handleHoverOut}>
          <View ref={overflowChipRef} style={[styles.chip, styles.overflowChip, { backgroundColor: chipColor }]}>
            <ThemedText style={styles.chipText}>+{overflow}</ThemedText>
          </View>
        </Pressable>
      )}

      {/* Hidden layer that measures every chip's natural width without affecting layout. */}
      <View style={styles.measureLayer} pointerEvents="none">
        {orderedValues.map((value) => renderChip(value, (w) => handleMeasureChip(value, w), "measure-"))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: GAP,
    alignItems: "center",
    overflow: "hidden",
    alignSelf: "stretch",
    width: "100%",
    minWidth: 0,
  },
  chip: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 10,
    marginVertical: 2,
  },
  chipText: {
    fontSize: 11,
    color: "white",
    fontWeight: "500",
  },
  overflowChip: {
    minWidth: 30,
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer"
  },
  measureLayer: {
    position: "absolute",
    flexDirection: "row",
    opacity: 0,
    left: 0,
    top: 0,
    zIndex: -1,
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 6,
    maxWidth: 380,
    alignItems: "flex-end",
  },
  typeIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
